import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useMeeting } from "../App";
import CustomerIntakeModal from "../components/CustomerIntakeModal";
import ScenarioSelectionModal from "../components/ScenarioSelectionModal";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Zap,
  Settings2,
  Cpu,
  PlayCircle,
  ArrowRight,
  History,
  Loader2,
  LogOut,
  User,
  FileText
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { loadMeeting } = useMeeting();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScenarioModalOpen, setIsScenarioModalOpen] = useState(false);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchRecentMeetings();
    }
  }, [token]);

  const fetchRecentMeetings = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentMeetings(response.data.slice(0, 3));
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectScenario = (scenario) => {
    // For custom scenarios, store in sessionStorage so presentation page can retrieve it
    if (scenario.isCustom) {
      sessionStorage.setItem('customScenario', JSON.stringify(scenario));
    }
    // Navigate to scenario presentation page with return location
    navigate(`/scenario/${scenario.id}?returnTo=/landing`);
  };

  const handleContinueMeeting = async (meetingId) => {
    try {
      await loadMeeting(meetingId);
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to load meeting");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const assetCards = [
    {
      icon: Zap,
      title: "Transformers",
      description: "Power & distribution transformer diagnostics",
      color: "text-amber-500",
      bgColor: "bg-amber-50"
    },
    {
      icon: Settings2,
      title: "Switchgear",
      description: "Circuit breaker & switchgear analysis",
      color: "text-blue-500",
      bgColor: "bg-blue-50"
    },
    {
      icon: Cpu,
      title: "Motors",
      description: "Rotating machinery health assessment",
      color: "text-green-500",
      bgColor: "bg-green-50"
    }
  ];

  return (
    <div className="landing-hero relative" data-testid="landing-page">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_5dae0428-c90f-47d3-96c5-04b06478a28c/artifacts/6qaxp7ot_DMS-logo.png" 
              alt="DMS Logo" 
              className="h-8 w-auto"
            />
            <span className="font-semibold text-heading text-slate-900 text-sm">Asset Atlas</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <User className="w-3 h-3" strokeWidth={1.5} />
              <span>{user?.name}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-600 h-8 text-xs"
              data-testid="header-logout-btn"
            >
              <LogOut className="w-3 h-3 mr-1" strokeWidth={1.5} />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-semibold text-heading text-slate-900 mb-3">
          Asset <span className="text-blue-600">Atlas</span>
        </h1>
        <p className="text-lg text-slate-500 mb-6 max-w-2xl mx-auto">
          CBM Diagnostic Prescription Engine. Guide customers through failure modes, testing methods, and monitoring solutions for industrial assets.
        </p>

        {/* Asset Preview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {assetCards.map((asset, index) => {
            const Icon = asset.icon;
            return (
              <Card key={index} className="border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-4 text-left">
                  <div className={`w-9 h-9 ${asset.bgColor} rounded-md flex items-center justify-center mb-2`}>
                    <Icon className={`w-4 h-4 ${asset.color}`} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-medium text-heading text-slate-900 text-sm">{asset.title}</h3>
                  <p className="text-xs text-slate-500">{asset.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Start Session Button */}
        <Button
          onClick={() => setIsModalOpen(true)}
          size="lg"
          className="bg-blue-600 hover:bg-blue-700 text-base px-6 py-4 h-auto"
          data-testid="start-session-btn"
        >
          <PlayCircle className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Start New Session
        </Button>

        {/* Load Scenarios Button */}
        <div className="mt-3">
          <Button
            onClick={() => setIsScenarioModalOpen(true)}
            variant="outline"
            size="sm"
            data-testid="load-scenarios-btn"
          >
            <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Load Diagnostic Scenarios
          </Button>
        </div>

        {/* Recent Sessions */}
        {recentMeetings.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <History className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <h2 className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Recent Sessions
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {recentMeetings.map((meeting) => (
                <button
                  key={meeting.id}
                  onClick={() => handleContinueMeeting(meeting.id)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:border-blue-500 hover:shadow-sm transition-all text-left"
                  data-testid={`recent-meeting-${meeting.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-900 truncate">
                      {meeting.customer_name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{meeting.company}</p>
                  </div>
                  <ArrowRight className="w-3 h-3 text-slate-400" strokeWidth={1.5} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Customer Intake Modal */}
      <CustomerIntakeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      
      {/* Scenario Selection Modal */}
      <ScenarioSelectionModal 
        isOpen={isScenarioModalOpen} 
        onClose={() => setIsScenarioModalOpen(false)}
        onSelectScenario={handleSelectScenario}
      />
    </div>
  );
};

export default LandingPage;
